import {
  Icon,
  IconButton,
  ModalBody,
  ModalCloseButton,
  Modal,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CellValue } from 'react-table';

import { IconPen, IconTrashbin } from 'assets/icons';
import Button from 'components/Button';
import { FormikInput } from 'components/Formik';
import { ConfirmationModal } from 'components/Modal';
import Table from 'components/Table';
import Title from 'components/Title';
import Tooltip from 'components/Tooltip';
import { useAssignPaychexWorkderId, IPaychexWorkerAssign } from 'hooks/integrations/useAssignPaychexWorkerId';
import {
  useFetchPaginatedPaychexEmployees,
  IPaychexEmployeeSearchQuery,
} from 'hooks/integrations/useFetchPaginatedPaychexEmployees';
import { useRemovePaychexWorkerId } from 'hooks/integrations/useRemovePaychexWorkerId';

import { initialPaychexWorkerFormData, paychexWorkerFormValidationSchema } from './schema';

const initialSearchQuery: IPaychexEmployeeSearchQuery = {
  page: 1,
  page_size: 25,
  search: '',
};

const PaychexWorker: React.FC<unknown> = () => {
  const [currentWorker, setCurrentWorker] = useState<any>(null);
  const [paychexWorkerFormValues, setPaychexWorkerFormValues] = useState<any>(initialPaychexWorkerFormData);
  const [searchQuery, setSearchQuery] = useState<IPaychexEmployeeSearchQuery>(initialSearchQuery);

  const { t } = useTranslation(['common', 'integrations', 'employees']);

  const { isOpen: isEditWorkerOpen, onOpen: onEditWorkerOpen, onClose: onEditWorkerClose } = useDisclosure();
  const {
    isOpen: isRemoveWorkerIDOpen,
    onOpen: onRemoveWorkerIDOpen,
    onClose: onRemoveWorkerIDClose,
  } = useDisclosure();

  const {
    data: paychexEmployeeData,
    status: paychexEmployeeStatus,
    refetch,
  } = useFetchPaginatedPaychexEmployees(searchQuery);
  const { status: paychexWorkerIDAssignStatus, mutateAsync: paychexWorkerIDAssignMutateAsync } =
    useAssignPaychexWorkderId();
  const { status: paychexWorkerIDRemoveStatus, mutateAsync: paychexWorkerIDRemoveMutateAsync } =
    useRemovePaychexWorkerId();

  const columns = [
    {
      Header: t('common:user'),
      accessor: 'name',
      Cell: (data: CellValue) => {
        return <Text>{data?.row?.original?.name}</Text>;
      },
    },
    {
      Header: t('integrations:workerId'),
      accessor: 'selected_paychex_worker',
      Cell: (data: CellValue) => {
        return <Text>{data?.row?.original?.selected_paychex_worker?.workerId}</Text>;
      },
    },
    {
      Header: '',
      accessor: 'first',
      width: 50,
      Cell: (data: CellValue) => {
        return (
          <div>
            <Tooltip label={t('integrations:editWorkerId')} hasArrow bg="black">
              <IconButton
                aria-label="Edit"
                bg="rgba(38, 103, 201, 0.1)"
                icon={<Icon as={IconPen} />}
                size="sm"
                mr="3"
                _hover={{ bg: 'rgba(38, 103, 201, 0.2)' }}
                onClick={() => onEdit(data?.row?.original)}
              />
            </Tooltip>
            <Tooltip label={t('integrations:deleteWorkerId')} hasArrow bg="black">
              <IconButton
                aria-label="Delete"
                bg="rgba(255, 83, 83, 0.1)"
                icon={<Icon as={IconTrashbin} />}
                size="sm"
                _hover={{ bg: 'rgba(255, 83, 83, 0.2)' }}
                onClick={() => onRemove(data?.row?.original)}
              />
            </Tooltip>
          </div>
        );
      },
    },
  ];

  const onRemove = (userData: any) => {
    setCurrentWorker(userData.id);
    onRemoveWorkerIDOpen();
  };

  const removeWorkerID = () => {
    paychexWorkerIDRemoveMutateAsync({ user_id: currentWorker }).then(() => {
      refetch();
      onRemoveClose();
    });
  };

  const onRemoveClose = () => {
    setCurrentWorker(null);
    onRemoveWorkerIDClose();
  };

  const onEdit = (userData: any) => {
    setCurrentWorker(userData.id);
    setPaychexWorkerFormValues({
      paychex_worker_id: userData.selected_paychex_worker ? userData.selected_paychex_worker?.workerId : '',
    });
    onEditWorkerOpen();
  };

  const onEditClose = () => {
    setCurrentWorker(null);
    onEditWorkerClose();
  };

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updatedSearchQuery = (previousSearchQuery: IPaychexEmployeeSearchQuery) => ({
      ...previousSearchQuery,
      page: selectedPageIndex + 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  const onWorkerIDSave = async (values: typeof initialPaychexWorkerFormData) => {
    const postData: IPaychexWorkerAssign = {
      user_id: currentWorker,
      paychex_worker_id: values.paychex_worker_id,
    };
    paychexWorkerIDAssignMutateAsync(postData).then(() => {
      refetch();
      onEditClose();
    });
  };

  return (
    <>
      <ConfirmationModal
        isOpen={isRemoveWorkerIDOpen}
        onClose={onRemoveClose}
        title={t('integrations:paychexCsv.removeWorkerIdConfirm')}
        subTitle=""
        confirmButtonLabel={t('integrations:remove')}
        confirmButtonProps={{
          bg: 'rgba(255, 83, 83, 0.1)',
          color: 'secondary.500',
          _hover: {
            bg: 'rgba(255, 83, 83, 0.2)',
          },
          isLoading: paychexWorkerIDRemoveStatus === 'loading',
          onClick: removeWorkerID,
        }}
      />

      <Modal isOpen={isEditWorkerOpen} onClose={onEditWorkerClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('integrations:assignWorkerId')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Formik
              enableReinitialize
              initialValues={paychexWorkerFormValues}
              validationSchema={paychexWorkerFormValidationSchema}
              onSubmit={onWorkerIDSave}
            >
              {() => (
                <Form noValidate>
                  <FormikInput
                    type="text"
                    name="paychex_worker_id"
                    label={t('integrations:workerId')}
                    formControlProps={{ isRequired: true }}
                  />
                  <Button mr={3} type="submit" isLoading={paychexWorkerIDAssignStatus === 'loading'}>
                    {t('common:save')}
                  </Button>
                  <Button onClick={onEditClose} variant="ghost">
                    {t('common:cancel')}
                  </Button>
                </Form>
              )}
            </Formik>
          </ModalBody>
        </ModalContent>
      </Modal>
      <Title>{t('integrations:paychexCsv.paychexMapping')}</Title>
      <Text variant="h2" fontSize="lg" ml={3} mt={4}>
        {t('integrations:paychexCsv.assignWorkerIdEmployee')}
      </Text>
      <Table
        columns={columns}
        data={paychexEmployeeData?.payload || []}
        isLoading={paychexEmployeeStatus === 'loading'}
        paginationData={paychexEmployeeData?.meta_data?.pagination}
        onPageChange={handlePageChange}
      />
    </>
  );
};
export default PaychexWorker;
